import { useApiClient } from '_core/api/context';
import { fetchAllPages } from '_core/api/fetchAllPages';
import { CenteredSpinner } from '_core/feedback/centeredSpinner';
import { GenericErrorMessage } from '_core/feedback/genericErrorMessage';
import { SearchForm } from '_core/forms/searchForm';
import { indexByLastItem } from '_core/indexBy';
import { Switch } from '_core/inputs/switch';
import { isNotNull } from '_core/isNotNull';
import { FormattedTitle } from '_core/react-head/formattedTitle';
import { TreeTableNode } from '_core/react-window/types';
import { findNode } from '_core/react-window/utils';
import { LinkButton } from '_core/router5/linkButton';
import {
  ISorting,
  SortingDirection,
  sortingFromRouterParam,
  sortingToRouterParam,
} from '_core/sorting';
import { Toolbar } from '_core/toolbar';
import { useAsyncData } from '_core/useAsyncData';
import { useDerivedState } from '_core/useDerivedState';
import { Button, Intent } from '@blueprintjs/core';
import { fetchConsigners } from 'consigners/api';
import { IConsigner } from 'consigners/types';
import { Col, Grid, Row, VGrid } from 'layout/contentLayout';
import {
  fetchManyPartners,
  IPartnerSerialized,
  PartnersSortingField,
} from 'partners/api';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useRouteNode } from 'react-router5';

import { ConsignersTreeTableNodeData } from './columns';
import { ConsignersListTable } from './listTable';
import { ConsignersTreeTable } from './treeTable';

interface IConsignersListAsyncData {
  consigners: IConsigner[];
  consignersByPartnerList: Array<[IPartnerSerialized | null, IConsigner[]]>;
  partnersById: Record<number, IPartnerSerialized>;
}

export default function ConsignersListRoute() {
  const { route, router } = useRouteNode('consigners');
  const api = useApiClient();

  const sorting = useMemo(
    () =>
      sortingFromRouterParam(route.params.sorting) || {
        field: 'created',
        direction: SortingDirection.Descending,
      },
    [route.params.sorting]
  );

  const { data, isFetching, refetch } = useAsyncData<IConsignersListAsyncData>(
    [api, route.params.search, sorting],

    async () => {
      const consignersSorting: ISorting[] = [];
      const partnersSorting: Array<ISorting<PartnersSortingField>> = [];

      if (sorting.field === 'consignerName') {
        consignersSorting.push({
          field: 'name',
          direction: sorting.direction,
        });
      } else if (sorting.field === 'name') {
        consignersSorting.push({
          field: 'name',
          direction: sorting.direction,
        });

        partnersSorting.push({
          field: PartnersSortingField.ShortName,
          direction: sorting.direction,
        });
      } else {
        consignersSorting.push(sorting);
      }

      const listResponse = await fetchAllPages(page =>
        fetchConsigners(api, {
          page,
          search: route.params.search,
          sorting: consignersSorting,
        })
      );

      const consigners = listResponse.results;

      const partnerIds = consigners
        .map(consigner => consigner.partner)
        .filter(isNotNull);

      const partners = await fetchManyPartners(api, partnerIds, {
        sorting: partnersSorting,
      });

      const partnersById = indexByLastItem(partners, partner => partner.id);

      const consignersWithNoPartner: IConsigner[] = [];
      const consignersByPartner: Record<number, IConsigner[]> = {};

      consigners.forEach(consigner => {
        const partner = consigner.partner;

        if (!partner) {
          consignersWithNoPartner.push(consigner);
          return;
        }

        consignersByPartner[partner] = consignersByPartner[partner] || [];
        consignersByPartner[partner].push(consigner);
      });

      const noPartnerGroup: [IPartnerSerialized | null, IConsigner[]] = [
        null,
        consignersWithNoPartner,
      ];

      const consignersByPartnerList: Array<
        [IPartnerSerialized | null, IConsigner[]]
      > = [noPartnerGroup].concat(
        partners.map((partner): [IPartnerSerialized, IConsigner[]] => [
          partner,
          consignersByPartner[partner.id],
        ])
      );

      return {
        consigners,
        consignersByPartnerList,
        partnersById,
      };
    }
  );

  const [nodes, setNodes] = useDerivedState<
    IConsignersListAsyncData | null,
    Array<TreeTableNode<ConsignersTreeTableNodeData>> | null
  >(
    data,
    (input, prevNodes) =>
      input &&
      input.consignersByPartnerList.map(
        ([partner, consigners]): TreeTableNode<ConsignersTreeTableNodeData> => {
          const prevPartnerNode = prevNodes
            ? findNode(
                prevNodes,
                node =>
                  node.data.kind === 'partner' &&
                  node.data.partner?.id === partner?.id
              )
            : undefined;

          return {
            id: `partner-${partner ? partner.id : 'null'}`,
            data: {
              kind: 'partner',
              partner,
              consignersCount: consigners.length,
            },
            children: {
              isExpanded:
                prevPartnerNode && prevPartnerNode.children
                  ? prevPartnerNode.children.isExpanded
                  : false,

              nodes: consigners.map(
                (consigner): TreeTableNode<ConsignersTreeTableNodeData> => ({
                  id: `consigner-${consigner.id}`,
                  data: { kind: 'consigner', consigner },
                })
              ),
            },
          };
        }
      )
  );

  const listParams = JSON.stringify(route.params);

  const applyFilterParams = useCallback(
    (filterParams: Record<string, string | undefined>) => {
      router.navigate(route.name, {
        ...route.params,
        page: undefined,
        ...filterParams,
      });
    },
    [route, router]
  );

  const handleSortingChange = useCallback(
    (newSorting: ISorting) => {
      applyFilterParams({ sorting: sortingToRouterParam(newSorting) });
    },
    [applyFilterParams]
  );

  const [renderTreeTable, setRenderTreeTable] = useState(true);

  return (
    <>
      <FormattedTitle>Грузоотправители</FormattedTitle>

      <VGrid stretch>
        <Row>
          <Grid>
            <Col span={3}>
              <SearchForm
                initialValue={route.params.search}
                onApply={search => {
                  applyFilterParams({ search });
                }}
              />
            </Col>

            <Col span={9}>
              <Toolbar align="right">
                <Button text="Обновить" onClick={refetch} />

                <LinkButton text="Сбросить фильтры" to={route.name} />

                <LinkButton
                  intent={Intent.PRIMARY}
                  params={{ listParams }}
                  text="Добавить"
                  to="consigners.create"
                />
              </Toolbar>
            </Col>
          </Grid>
        </Row>

        <Row>
          <Switch
            checked={renderTreeTable}
            label="Группировать по контрагенту"
            onChange={newChecked => {
              setRenderTreeTable(newChecked);
              applyFilterParams({ sorting: undefined });
            }}
          />
        </Row>

        <Row stretch>
          {!data || !nodes ? (
            isFetching ? (
              <CenteredSpinner />
            ) : (
              <GenericErrorMessage />
            )
          ) : renderTreeTable ? (
            <ConsignersTreeTable
              isFetching={isFetching}
              nodes={nodes}
              sorting={sorting}
              onNodesChange={setNodes}
              onSortingChange={handleSortingChange}
            />
          ) : (
            <ConsignersListTable
              consigners={data.consigners}
              isFetching={isFetching}
              listParams={listParams}
              partnersById={data.partnersById}
              sorting={sorting}
              onSortingChange={handleSortingChange}
            />
          )}
        </Row>
      </VGrid>
    </>
  );
}
